import React from 'react';

import { Label } from 'twyne-new.ui';

import appStore from '@Stores/app';
import InfoTip from 'components/InfoTip';
import HierarchicalReportLayout, { formatters } from 'components/hierarchical-report/Layout';

import lossesStore from '../../stores/losses';

import Summary from './components/Summary'
import { LossesReportProvider, useLossesReportStore } from './store';

export default class LossesLayout extends HierarchicalReportLayout {
  store = lossesStore
  title = this.props.title || 'Pub Performance'

  useNewStore = () => {
    const store = useLossesReportStore()
    const { report: { useLoad } } = store
    const { detachedSite } = this.props
    useLoad(null, !detachedSite)
    return store
  }

  componentDidMount() {
    this.store.loadPostContentTypes();
  }

  get configure() {
    const getDisabled = isRevGroup => groupby => isRevGroup ? !isRevGroup : groupby.some(group => group.startsWith('rev__'))
    return {
      filterby: {
        options: [
          {
            value: 'site',
            get label() {
              return <formatters.Loadable isLoading={lossesStore.site.isLoading}>Site</formatters.Loadable>
            },
            options: () => lossesStore.site.fetchData(this.props.detachedSite),
          },
          {
            value: 'publisher',
            get label() {
              return <formatters.Loadable isLoading={lossesStore.publisher.isLoading}>Publisher</formatters.Loadable>
            },
            options: lossesStore.publisher.fetchData,
          },
          {
            value: 'allocation',
            get label() {
              return <formatters.Loadable isLoading={lossesStore.allocation.isLoading}>Allocation</formatters.Loadable>
            },
            options: () => lossesStore.allocation.fetchData(this.props.detachedSite),
          },
          {
            value: 'post_target',
            get label() {
              return <formatters.Loadable isLoading={lossesStore.post.isLoading}>Post target</formatters.Loadable>
            },
            options: () => lossesStore.post.fetchData(this.props.detachedSite),
          },
          {
            value: 'post',
            get label() {
              return <formatters.Loadable isLoading={lossesStore.post.isLoading}>Post</formatters.Loadable>
            },
            options: () => lossesStore.post.fetchData(this.props.detachedSite),
          },
          {
            value: 'payouttype',
            get label() {
              return <formatters.Loadable>Payout type</formatters.Loadable>
            },
            options: async () => [
              { value: "cpc", label: "CPC", getLabel: () => "CPC", getValue: () => "cpc" },
              { value: "cpa", label: "CPA", getLabel: () => "CPA", getValue: () => "cpa" },
              { value: "cpm", label: "CPM", getLabel: () => "CPM", getValue: () => "cpm" },
              { value: "cpl", label: "CPL", getLabel: () => "CPL", getValue: () => "cpl" },
              { value: "custom", label: "CUSTOM", getLabel: () => "CUSTOM", getValue: () => "custom" },
            ],
          },
          {
            value: 'fraudScore',
            get label() {
              return <formatters.Loadable>Fraud Score</formatters.Loadable>
            },
            options: async () => [
              { value: "good", label: "Good", getLabel: () => "Good", getValue: () => "good" },
              { value: "bad", label: "Bad", getLabel: () => "Bad", getValue: () => "bad" },
              { value: "warn", label: "Warn", getLabel: () => "Warn", getValue: () => "warn" },
              { value: "drop", label: "Drop", getLabel: () => "Drop", getValue: () => "drop" },
              { value: "throttle", label: "Throttle", getLabel: () => "Throttle", getValue: () => "throttle" },
              { value: "awaiting", label: "Awaiting", getLabel: () => "Awaiting", getValue: () => "awaiting" },
            ],
          },
          {
            value: 'domain',
            get label() {
              return <formatters.Loadable>Domain</formatters.Loadable>
            },
            options: async () => appStore.site.domain_aliases
              .split(",")
              .filter(value => !value?.includes("*"))
              .map(value => {
                value = JSON.parse(value)
                return { label: value, value, getLabel: () => value, getValue: () => value }
              }),
          },
        ].filter(option => (this.props.detachedSite && option.value !== 'domain') || (!this.props.detachedSite && option.value !== 'site')),
      },
      groupby: {
        default: this.props.detachedSite ? ['site'] : ['publisher', 'subId'],
        options: [
          { value: 'publisher', label: 'Publisher', group: 'Traffic', named: true, disabled: getDisabled(false) },
          { value: 'allocation', label: 'Allocation', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'offer', label: 'Offer', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'campaignid', label: 'Campaign Id', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'conversiontype', label: 'Conversion Type', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'payouttype', label: 'Payout Type', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'subId', label: 'SubID 1', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'subId2', label: 'SubID 2', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'subId3', label: 'SubID 3', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'duplicatedData', label: 'Duplicated Data', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'duplicatedClick', label: 'Duplicated Click', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'duplicated', label: 'duplicated', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'dupeDaysDelta', label: 'Dupe Days Delta', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'domain', label: 'Domain', group: 'Traffic', disabled: getDisabled(false) },

          { value: 'converted', label: 'converted', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'isLoss', label: 'Is Loss', group: 'Traffic', disabled: getDisabled(false) },
          { value: 'rev__origin', label: 'Revenue Origin', group: 'Traffic', disabled: getDisabled(true) },
          { value: 'rev__externalId', label: 'Revenue External ID', group: 'Traffic', disabled: getDisabled(true) },
          { value: 'rev__integrationId', label: 'Revenue Integration ID', group: 'Traffic', disabled: getDisabled(true) },

          { value: 'registration_completed', label: 'Completed', group: 'Registration', disabled: getDisabled(false) },
          { value: 'registrationTotalSteps', label: 'Total Steps', group: 'Registration', disabled: getDisabled(false) },
          { value: 'registrationLastStep', label: 'Last Step', group: 'Registration', disabled: getDisabled(false) },
          { value: 'tcpaConsent', label: 'TCPA Consent', group: 'Registration', disabled: getDisabled(false) },
          { value: 'skipRegistration', label: 'Skipped', group: 'Registration', disabled: getDisabled(false) },
          { value: 'registrationConsent', label: 'Reg. Consent', group: 'Registration', disabled: getDisabled(false) },
          { value: 'registrationCount', label: 'Registrations Count', group: 'Consumer', disabled: getDisabled(false) },

          { value: 'fraud_verification', label: 'Fraud', group: 'Verifications', disabled: getDisabled(false) },
          { value: 'fraudScore', label: 'Fraud Score', group: 'Verifications', disabled: getDisabled(false) },
          { value: 'emailVerification', label: 'Email', group: 'Verifications', disabled: getDisabled(false) },
          { value: 'addressVerification', label: 'Address', group: 'Verifications', disabled: getDisabled(false) },
          { value: 'botVerification', label: 'Bot', group: 'Verifications', disabled: getDisabled(false) },
          { value: 'postalVerification', label: 'Postal', group: 'Verifications', disabled: getDisabled(false) },
          { value: 'phoneVerification', label: 'Phone', group: 'Verifications', disabled: getDisabled(false) },

          { value: 'form', label: 'Form', group: 'Form', disabled: getDisabled(false) },
          { value: 'formConversionStep', label: 'Conversion Step', group: 'Form', disabled: getDisabled(false) },

          { value: 'site', label: 'Site', group: 'Organization' },

          { value: 'hour', label: 'Hour', group: 'Full Time', disabled: getDisabled(false) },
          { value: 'day', label: 'Day', group: 'Full Time', disabled: getDisabled(false) },
          { value: 'month', label: 'Month', group: 'Full Time', disabled: getDisabled(false) },
          { value: 'year', label: 'Year', group: 'Full Time', disabled: getDisabled(false) },

          // { value: 'hour_unittime', label: 'Hour', group: 'Unit Time', disabled: getDisabled(false) },
          // { value: 'day_unittime', label: 'Day', group: 'Unit Time', disabled: getDisabled(false) },
          // { value: 'weekday_unittime', label: 'Weekday', group: 'Unit Time', disabled: getDisabled(false) },
          // { value: 'month_unittime', label: 'Month', group: 'Unit Time', disabled: getDisabled(false) },

          { value: 'device', label: 'Device', group: 'Request', disabled: getDisabled(false) },
          { value: 'os', label: 'OS', group: 'Request', disabled: getDisabled(false) },
          { value: 'browser', label: 'Browser', group: 'Request', disabled: getDisabled(false) },

          { value: 'post', label: 'Post', group: 'CMS', disabled: getDisabled(false) },
          { value: 'post_version', label: 'Post Version', group: 'CMS', disabled: getDisabled(false) },
          { value: 'post_target', label: 'Post Target', group: 'CMS', disabled: getDisabled(false) },

          { value: 'city', label: 'City', group: 'Consumer', disabled: getDisabled(false) },
          { value: 'state', label: 'State', group: 'Consumer', disabled: getDisabled(false) },
          { value: 'emaildomain', label: 'Email Domain', group: 'Consumer', disabled: getDisabled(false) },
          { value: 'age', label: 'Age', group: 'Consumer', disabled: getDisabled(false) },
          { value: 'age_legacy', label: 'Age (legacy)', group: 'Consumer', disabled: getDisabled(false) },
          { value: 'gender', label: 'Gender', group: 'Consumer', disabled: getDisabled(false) },
          { value: 'optout', label: 'optout', group: 'Consumer', disabled: getDisabled(false) },
          {
            group: 'Meta Fields',
            options: lossesStore.metaField.fetchData,
            disabled: getDisabled(false),
          },
        ].filter(option => this.props.detachedSite || (!this.props.detachedSite && option.value !== 'site')),
      },
      metrics: {
        default: this.props.detachedSite ? ['total', 'completed', 'duplicated', 'cost', 'revenue', 'margin', 'rps', 'rpu'] : ['completed', 'duplicated', 'tcpaconsent', 'converted', 'convertedTrue', 'rps', 'rpu'],
        options: [
          { value: 'converted', label: 'Conversion', group: 'Built-in' },
          { value: 'convertedTrue', label: 'Form Converted', group: 'Built-in' },
          { value: 'duplicated', label: 'Duplicated', group: 'Built-in' },
          { value: 'duplicatedData', label: 'Duplicated Data', group: 'Built-in' },
          { value: 'duplicatedClick', label: 'Duplicated Click', group: 'Built-in' },
          { value: 'tcpaconsent', label: 'TCPA Consent', group: 'Built-in' },
          { value: 'completed', label: 'Completed', group: 'Built-in' },
          { value: 'uniqUser', label: 'Unique Users', group: 'Built-in' },
          { value: 'fraudConversion', label: 'Conv. Type is Fraud', group: 'Built-in' },
          { value: 'fraudCheckTrue', label: 'Is fraud', group: 'Built-in' },
          { value: 'fraudCheckFalse', label: 'Is Not fraud', group: 'Built-in' },
          { value: 'fraudNotCheck', label: 'Fraud not verified', group: 'Built-in' },
          { value: 'awaitingFraudCheck', label: 'Awaiting Fraud Verification', group: 'Built-in' },
          { value: 'scrub', label: 'Scrub', group: 'Built-in' },

          { value: 'convertedPC', label: 'Conversion %', group: 'Ratio' },
          { value: 'convertedTruePC', label: 'Form Converted %', group: 'Ratio' },
          { value: 'duplicatedPC', label: 'Duplicated %', group: 'Ratio' },
          { value: 'duplicatedDataPC', label: 'Duplicated Data %', group: 'Ratio' },
          { value: 'duplicatedClickPC', label: 'Duplicated Click %', group: 'Ratio' },
          { value: 'tcpaconsentPC', label: 'TCPA Consent %', group: 'Ratio' },
          { value: 'completedPC', label: 'Completed %', group: 'Ratio' },
          { value: 'uniqUserPC', label: 'Unique Users %', group: 'Ratio' },
          { value: 'fraudConversionPC', label: 'Conv. Type is Fraud %', group: 'Ratio' },
          { value: 'fraudCheckTruePC', label: 'Is fraud %', group: 'Ratio' },
          { value: 'fraudCheckFalsePC', label: 'Is not fraud %', group: 'Ratio' },
          { value: 'fraudNotCheckPC', label: 'Fraud not verified %', group: 'Ratio' },
          { value: 'awaitingFraudCheckPC', label: 'Awaiting Fraud Verification %', group: 'Ratio' },
          { value: 'fraudProjectedPC', label: 'Projected fraud %', group: 'Ratio' },
          { value: 'scrubPC', label: 'Scrub %', group: 'Ratio' },
          { value: 'marginPC', label: 'Margin %', group: 'Ratio' },

          { value: 'rps', label: <span>RPS $ (<small><i>Rev. / Session</i></small>)</span>, group: 'Derived' },
          { value: 'rpu', label: <span>RPU $ (<small><i>Rev. / Users</i></small>)</span>, group: 'Derived' },

          { value: 'cps', label: <span>CPS $ (<small><i>Cost. / Session</i></small>)</span>, group: 'Derived' },
          { value: 'cpu', label: <span>CPU $ (<small><i>Cost. / Users</i></small>)</span>, group: 'Derived' },

          { value: 'cpc', label: <span>CPC $ (<small><i>Cost. / Conversion</i></small>)</span>, group: 'Derived' },
          { value: 'rpc', label: <span>RPC $ (<small><i>Rev. / Conversion</i></small>)</span>, group: 'Derived' },
          { value: 'cpfc', label: <span>CPFC $ (<small><i>Cost. / Form Converted</i></small>)</span>, group: 'Derived' },
          { value: 'rpfc', label: <span>RPFC $ (<small><i>Rev. / Form Converted</i></small>)</span>, group: 'Derived' },
        ],
      },
      sortby: {
        default: ['total desc'],
        options: [
          { value: 'total', label: 'Session', group: 'Default' },
          { value: 'revenue', label: 'Revenue', group: 'Default' },
          { value: 'cost', label: 'Cost', group: 'Default' },
          { value: 'margin', label: 'Margin', group: 'Default' },
        ],
      },
    }
  }
  get columns() {
    return [...this.buildColumns()]
  }

  * buildColumns() {
    const { metrics, groupby } = this.state || {}

    yield {
      dataField: 'value',
      text: groupby && groupby.map(formatters.groupsText).join(' / '),
      customSort: true,
      sortValue: row => row.name || row.keyString || row.value,
      formatter: formatters.name,
    }

    yield {
      dataField: 'total',
      customSort: true,
      text: "Session",
      formatter: formatters.drilldown(this.newTab, 'total'),
    }

    if (metrics) {
      for (const metricColumn of this.metricColumns) {
        if (metrics.includes(metricColumn.dataField)) {
          yield metricColumn
        }
      }
    }

    yield {
      dataField: 'cost',
      customSort: true,
      text: 'Cost ($)',
      formatter: formatters.parentPercent(formatters.money(), 'cost'),
    }

    yield {
      dataField: 'revenue',
      customSort: true,
      text: "Revenue ($)",
      formatter: formatters.parentPercent(formatters.money(), 'revenue'),
    }

    yield {
      dataField: 'margin',
      customSort: true,
      text: "Margin ($)",
      formatter: formatters.parentPercent(cell => <>{
        formatters.colorize(formatters.money()(cell), cell)
      }</>, 'revenue'),
    }

    if (metrics && metrics.includes('marginPC')) {
      yield {
        dataField: 'marginPC',
        customSort: true,
        text: "Margin (%)",
        formatter: cell => formatters.colorize(formatters.percent(cell), cell),
      }
    }
  }

  summaryCards = [
    {
      title: "Sessions",
      get value() {
        return lossesStore.sessions
      },
    }, {
      title: "Registration Completed",
      get value() {
        return lossesStore.completed
      },
    }, {
      title: "Revenue",
      money: true,
      get value() {
        return lossesStore.revenue
      },
    }, {
      title: "Cost",
      money: true,
      get value() {
        return lossesStore.cost
      },
    }, {
      title: "Margin",
      money: true,
      get value() {
        return lossesStore.margin
      },
    },
  ]

  metricColumns = [{
    dataField: 'completed',
    customSort: true,
    text: "Completed",
    formatter: formatters.drilldown(this.newTab, 'completed'),
  }, {
    dataField: 'completedPC',
    customSort: true,
    text: "Completed (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'uniqueReg',
    customSort: true,
    text: "Uniq. Reg.",
    formatter: formatters.parentPercent(formatters.number, 'uniqueReg'),
  }, {
    dataField: 'uniqueRegPC',
    customSort: true,
    text: "Uniq. Reg. (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'uniqUser',
    customSort: true,
    text: "Uniq. Users",
    formatter: formatters.parentPercent(formatters.number, 'uniqUser'),
  }, {
    dataField: 'uniqUserPC',
    customSort: true,
    text: "Uniq. Users (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'converted',
    customSort: true,
    text: <Label>Conversion <InfoTip side='bottom' color='neutral' id='converted'>The conversion pixel fired and cost attributed.</InfoTip></Label>,
    formatter: formatters.drilldown(this.newTab, 'converted'),
  }, {
    dataField: 'convertedTrue',
    customSort: true,
    text: <Label>Form Converted <InfoTip side='bottom' color='neutral' id='convertedTrue'>The conversion step on the form was completed.</InfoTip></Label>,
    formatter: formatters.drilldown(this.newTab, 'formConverted'),
  }, {
    dataField: 'convertedPC',
    customSort: true,
    text: "Conversion (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'convertedTruePC',
    customSort: true,
    text: "Form Converted (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'scrub',
    customSort: true,
    text: <Label>Scrub <InfoTip side='bottom' color='neutral' id='scrub'>Total of conversion-type equal to scrub or scrub bucket. <br /> f(x) = (scrub + scrub_bucket)</InfoTip></Label>,
    formatter: formatters.drilldown(this.newTab, 'scrub'),
  }, {
    dataField: 'scrubPC',
    customSort: true,
    text: <Label>Scrub (%) <InfoTip side='bottom' color='neutral' id='scrubPC'>f(x) = (scrub + scrub_bucket) / (scrub + scrub_bucket + converted) </InfoTip></Label>,
    formatter: (cell, { converted, scrub }) => formatters.percent(cell, scrub + converted),
  }, {
    dataField: 'duplicated',
    customSort: true,
    text: "Duplicated",
    formatter: formatters.drilldown(this.newTab, 'duplicated'),
  }, {
    dataField: 'duplicatedPC',
    customSort: true,
    text: "Duplicated (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'duplicatedData',
    customSort: true,
    text: "Duplicated Data",
    formatter: formatters.drilldown(this.newTab, 'duplicatedData'),
  }, {
    dataField: 'duplicatedDataPC',
    customSort: true,
    text: "Duplicated Data (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'duplicatedClick',
    customSort: true,
    text: "Duplicated Click",
    formatter: formatters.drilldown(this.newTab, 'duplicatedClick'),
  }, {
    dataField: 'duplicatedClickPC',
    customSort: true,
    text: "Duplicated Click (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'tcpaconsent',
    customSort: true,
    text: "TCPA consent",
    formatter: formatters.drilldown(this.newTab, 'tcpaconsent'),
  }, {
    dataField: 'tcpaconsentPC',
    customSort: true,
    text: "TCPA consent (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'fraudConversion',
    customSort: true,
    text: <Label>Conv. Type is Fraud <InfoTip side='bottom' color='neutral' id='fraudConversion'>The conversion type is fraud.</InfoTip></Label>,
    formatter: formatters.drilldown(this.newTab, 'fraudConversion'),
  }, {
    dataField: 'fraudCheckTrue',
    customSort: true,
    text: <Label>Is fraud <InfoTip side='bottom' color='neutral' id='fraudCheckTrue'>The fraud verification service return fraud (bad or drop score).</InfoTip></Label>,
    formatter: formatters.drilldown(this.newTab, 'fraudCheckTrue'),
  }, {
    dataField: 'fraudCheckFalse',
    customSort: true,
    text: <Label>Is Not fraud <InfoTip side='bottom' color='neutral' id='fraudCheckFalse'>The fraud verification service return is not fraud (good or warn score).</InfoTip></Label>,
    formatter: formatters.drilldown(this.newTab, 'fraudCheckFalse'),
  }, {
    dataField: 'fraudNotCheck',
    customSort: true,
    text: <Label>Fraud not verified <InfoTip side='bottom' color='neutral' id='fraudNotCheck'>The fraud verification service not handled (service disabled on allocation, site or the fraud throttle was not satisfied).</InfoTip></Label>,
    formatter: formatters.drilldown(this.newTab, 'fraudNotCheck'),
  }, {
    dataField: 'awaitingFraudCheck',
    customSort: true,
    text: <Label>Awaiting Fraud Verification <InfoTip side='left' color='neutral' id='awaitingFraudCheck'>The fraud verification service not handled (service enabled on allocation or site, but verification was blocked or not executed).</InfoTip></Label>,
    formatter: formatters.drilldown(this.newTab, 'awaitingFraudCheck'),
  }, {
    dataField: 'fraudConversionPC',
    customSort: true,
    text: "Conv. Type is Fraud (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'fraudCheckTruePC',
    customSort: true,
    text: "Is fraud (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'fraudCheckFalsePC',
    customSort: true,
    text: "Is not fraud (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'fraudNotCheckPC',
    customSort: true,
    text: "Fraud not verified (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'awaitingFraudCheckPC',
    customSort: true,
    text: "Awaiting Fraud Verification (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'fraudProjectedPC',
    customSort: true,
    text: "Projected fraud (%)",
    formatter: cell => formatters.percent(cell),
  }, {
    dataField: 'rps',
    customSort: true,
    text: "RPS",
    formatter: formatters.money(),
  }, {
    dataField: 'rpu',
    customSort: true,
    text: "RPU",
    formatter: formatters.money(),
  }, {
    dataField: 'cps',
    customSort: true,
    text: "CPS",
    formatter: formatters.money(),
  }, {
    dataField: 'cpu',
    customSort: true,
    text: "CPU",
    formatter: formatters.money(),
  }, {
    dataField: 'cpc',
    customSort: true,
    text: <Label>CPC <InfoTip side='bottom' color='neutral' id='cpc'>Cost per Conversion</InfoTip></Label>,
    formatter: formatters.money(),
  }, {
    dataField: 'rpc',
    customSort: true,
    text: <Label>RPC <InfoTip side='bottom' color='neutral' id='rpc'>Revenue per Conversion</InfoTip></Label>,
    formatter: formatters.money(),
  }, {
    dataField: 'cpfc',
    customSort: true,
    text: <Label>CPFC <InfoTip side='bottom' color='neutral' id='cpfc'>Cost per Form Converted</InfoTip></Label>,
    formatter: formatters.money(),
  }, {
    dataField: 'rpfc',
    customSort: true,
    text: <Label>RPFC <InfoTip side='bottom' color='neutral' id='rpfc'>Revenue per Form Converted</InfoTip></Label>,
    formatter: formatters.money(),
  }]

  Summary = () => {
    if (this.summaryCards) {
      return <Summary key='summary' cards={this.summaryCards} isLoading={this.store.collection.isLoading} />
    }
    return null
  }

  ReportProvider = ({ children }) => <LossesReportProvider>
    {children}
  </LossesReportProvider>
}
